import { useId, useState } from "react";
import "./templates.css";

type StyleId = "minimal" | "premium" | "natural" | "food" | "tech" | "sport" | "market" | "creator";

type StyleOption = {
  id: StyleId;
  label: string;
  caption: string;
  glyph: string;
  brand: string;
  title: string;
  subtitle: string;
  accent: string;
  emotion: string;
  tags: [string, string, string];
};

const styles: StyleOption[] = [
  { id: "minimal", label: "MINIMAL", caption: "Fashion / Design", glyph: "◇", brand: "MAISON", title: "Minimalismus, který dýchá.", subtitle: "Čistota, prostor a prémiová jednoduchost.", accent: "stone", emotion: "Klid, ve kterém vynikne to podstatné.", tags: ["Typografie", "Prostor", "Jasná nabídka"] },
  { id: "premium", label: "PREMIUM", caption: "Beauty / Jewellery", glyph: "♕", brand: "LUMIÈRE", title: "Luxus v každém detailu.", subtitle: "Silná vizuální identita a vysoká vnímaná hodnota.", accent: "rose", emotion: "Detail, který mění zájem v touhu.", tags: ["Elegance", "Detail", "Prémiový dojem"] },
  { id: "natural", label: "NATURAL", caption: "Nature / Wellness", glyph: "◌", brand: "BOTANICA", title: "Přirozeně. Ve vlastním rytmu.", subtitle: "Jemný přírodní styl pro wellness, jógu a kreativní značky.", accent: "sage", emotion: "Pocit, že jste na správném místě.", tags: ["Klid", "Příběh", "Přirozenost"] },
  { id: "food", label: "FOOD", caption: "Gastro / Delicacies", glyph: "⌑", brand: "MONSIEUR", title: "Chuť začíná pohledem.", subtitle: "Produkt v centru, jasná nabídka a hladká cesta k objednávce.", accent: "amber", emotion: "Chuť, kterou cítíte ještě před první objednávkou.", tags: ["Produkt", "Atmosféra", "Objednávka"] },
  { id: "tech", label: "TECH", caption: "Electronics / B2B", glyph: "▦", brand: "NEXTECH", title: "Technologie, které posouvají vpřed.", subtitle: "Přesné informace, rychlé srovnání a důvěryhodný výkon.", accent: "graphite", emotion: "Jistota, že i složité věci mohou být jednoduché.", tags: ["Přehled", "Srovnání", "Výkon"] },
  { id: "sport", label: "SPORT", caption: "Fitness / Outdoor", glyph: "⌁", brand: "NORTH", title: "Pohyb bez kompromisů.", subtitle: "Energie, výkon a silná mobilní konverze.", accent: "aqua", emotion: "Energie, která vás nenechá stát na místě.", tags: ["Energie", "Výkon", "Mobilní nákup"] },
  { id: "market", label: "MARKET", caption: "Large Catalogues", glyph: "⌗", brand: "DOMOVIA", title: "Velký výběr. Žádný chaos.", subtitle: "Přehledné kategorie, filtrace a rychlé rozhodování.", accent: "sand", emotion: "Radost z výběru bez ztráty přehledu.", tags: ["Kategorie", "Filtrace", "Orientace"] },
  { id: "creator", label: "CREATOR", caption: "Small Brands", glyph: "▣", brand: "ATELIER", title: "Značka s lidskou tváří.", subtitle: "Příběh, důvěra a prodej v jednom kompaktním systému.", accent: "peach", emotion: "Příběh, který má vlastní tvář i hlas.", tags: ["Osobnost", "Příběh", "Komunita"] }
];

const miniStyles: StyleId[] = ["minimal", "premium", "natural", "food", "tech", "sport", "market", "creator"];

export function getRequestedStyle(): StyleId | undefined {
  const requested = new URLSearchParams(window.location.search).get("styl");
  return styles.find(style => style.id === requested)?.id;
}

const stylePreviews: Record<StyleId, string> = {
  minimal: "/template-previews/minimal.webp",
  premium: "/template-previews/premium.webp",
  natural: "/visuals/natural-card.webp",
  food: "/template-previews/food.webp",
  tech: "/template-previews/tech.webp",
  sport: "/template-previews/sport.webp",
  market: "/template-previews/market.webp",
  creator: "/template-previews/creator.webp"
};

const styleGallery: Record<StyleId, string> = {
  minimal: "/style-gallery/minimal.webp",
  premium: "/style-gallery/premium.webp",
  natural: "/visuals/natural-hero.webp",
  food: "/style-gallery/food.webp",
  tech: "/style-gallery/tech.webp",
  sport: "/style-gallery/sport.webp",
  market: "/style-gallery/market.webp",
  creator: "/style-gallery/creator.webp"
};


function StyleTabs({
  selected,
  onSelect,
  tabsId
}: {
  selected: StyleId;
  onSelect: (id: StyleId) => void;
  tabsId: string;
}) {
  return (
    <div className="style-tabs" role="tablist" aria-label="Styly webů">
      {styles.map((style) => (
        <button
          key={style.id}
          className={selected === style.id ? "active" : ""}
          type="button"
          role="tab"
          id={`${tabsId}-${style.id}`}
          aria-controls={`${tabsId}-panel`}
          aria-selected={selected === style.id}
          tabIndex={selected === style.id ? 0 : -1}
          onClick={() => onSelect(style.id)}
          onKeyDown={(event) => {
            const index = styles.findIndex(item => item.id === style.id);
            const nextIndex = event.key === "ArrowRight" ? (index + 1) % styles.length
              : event.key === "ArrowLeft" ? (index - 1 + styles.length) % styles.length
              : event.key === "Home" ? 0 : event.key === "End" ? styles.length - 1 : undefined;
            if (nextIndex === undefined) return;
            event.preventDefault();
            onSelect(styles[nextIndex].id);
            document.getElementById(`${tabsId}-${styles[nextIndex].id}`)?.focus();
          }}
        >
          <span aria-hidden="true">{style.glyph}</span>
          {style.label}
        </button>
      ))}
    </div>
  );
}

function ProductCard({ index, current }: { index: number; current: StyleOption }) {
  const names =
    current.id === "natural"
      ? ["Botanical", "Wellness Ritual", "Natural Essentials"]
      : current.id === "food"
        ? ["Signature výběr", "Degustační box", "Bestseller"]
        : current.id === "tech"
          ? ["Core One", "Studio Pro", "Air Max"]
          : ["Signature", "Collection 02", "Bestseller"];
  return (
    <article className="store-product">
      <div className={"product-visual pv-" + index}>
        <img src={current.id === "natural" ? "/visuals/natural-product.webp" : stylePreviews[current.id]} alt="" loading="lazy" />
      </div>
      <small>{index === 0 ? "Bestseller" : "Novinka"}</small>
      <strong>{names[index] ?? names[0]}</strong>
      <b>{index === 0 ? "249 Kč" : index === 1 ? "229 Kč" : "279 Kč"}</b>
    </article>
  );
}

function StoreMockup({ current }: { current: StyleOption }) {
  
  return (
    <div className={"mockup-scene template-" + current.id + " accent-" + current.accent} inert aria-hidden="true">
      <div className="browser">
        <div className="browser-bar">
          <span className="dots"><i /><i /><i /></span>
          <span className="address">{"www." + current.brand.toLowerCase().replaceAll(" ", "") + ".cz"}</span>
          <span />
        </div>
        <div className="shop">
          <div className="shop-nav">
            <b>{current.glyph} {current.brand}</b>
            <div>Produkty&nbsp;&nbsp;&nbsp; Dárkové sady&nbsp;&nbsp;&nbsp; Příběh&nbsp;&nbsp;&nbsp; Blog</div>
            <span>⌕ &nbsp; ♙ &nbsp; ♡</span>
          </div>
          <div className="shop-hero">
            <div className="shop-copy">
              <h2>{current.title}</h2>
              <p>{current.subtitle}</p>
              <button type="button">Objevit kolekci →</button>
            </div>
            <div className="hero-still hero-still-photo" aria-hidden="true">
              <img src={styleGallery[current.id]} alt="" loading="lazy" />
              <span>{current.label}</span>
            </div>
          </div>
          <div className="shop-benefits">
            <span>◌ <b>Přírodní<br />složení</b></span>
            <span>▱ <b>Rychlé<br />doručení</b></span>
            <span>□ <b>Dárkové<br />balení</b></span>
            <span>♡ <b>Zákaznická<br />péče</b></span>
          </div>
        </div>
      </div>

      <div className="tablet">
        <div className="tablet-inner">
          <div className="tablet-top"><b>{current.brand}</b><span>⌕</span></div>
          <h4>Naše bestsellery</h4>
          <div className="tablet-products">
            <ProductCard index={0} current={current} />
            <ProductCard index={1} current={current} />
          </div>
        </div>
      </div>

      <div className="mobile">
        <div className="mobile-inner">
          <div className="mobile-top"><b>{current.brand}</b><span>♡</span></div>
          <div className="mobile-image mobile-image-photo">
            <img src={styleGallery[current.id]} alt="" loading="lazy" />
          </div>
          <h4>{current.title}</h4>
          <small>Ukázka produktové stránky</small>
          <b>229 Kč</b>
          <button type="button">Přidat do košíku</button>
        </div>
      </div>

      <div className="recommend-badge">☆ Doporučený směr</div>
    </div>
  );
}

function MiniCard({ style, selected, onSelect }: { style: StyleOption; selected: boolean; onSelect: (id: StyleId) => void }) {
  return (
    <button className={"mini-card mini-" + style.accent + (selected ? " active" : "")} type="button" aria-pressed={selected} onClick={() => onSelect(style.id)}>
      <div className="mini-shot mini-shot-real">
        <img src={styleGallery[style.id]} alt={"Vizuální inspirace směru " + style.label} loading="lazy" />
      </div>
      <div className="mini-copy">
        <div><b>{style.label}</b><span>{style.caption}</span></div>
        <em>Zobrazit styl →</em>
      </div>
    </button>
  );
}

export default function TemplateShowcase({ showHeading = false }: { showHeading?: boolean }) {
  const tabsId = useId();
  const [selected, setSelected] = useState<StyleId>(() => {
    const fromUrl = getRequestedStyle();
    if (fromUrl) return fromUrl;
    try {
      const stored = window.localStorage.getItem("onyxweb:preferred-style");
      const saved = styles.find(style => style.id === stored);
      if (saved) return saved.id;
    } catch { /* Private browsing may disable storage. */ }
    return "minimal";
  });
  const selectStyle = (id: StyleId) => {
    setSelected(id);
    try { window.localStorage.setItem("onyxweb:preferred-style", id); } catch { /* optional */ }
  };
  const [prompt, setPrompt] = useState("");
  const current = styles.find(style => style.id === selected) ?? styles[2];
  const recommend = () => {
    const q = prompt.toLocaleLowerCase("cs");
    let id: StyleId = "minimal";

    if (["čaj", "wellness", "bio", "přírod", "jóga", "zdrav"].some((word) => q.includes(word))) {
      id = "natural";
    } else if (["luxus", "šperk", "kosmet", "premium", "salon", "krása"].some((word) => q.includes(word))) {
      id = "premium";
    } else if (["gastro", "jíd", "restaur", "bistro", "kavár", "delik"].some((word) => q.includes(word))) {
      id = "food";
    } else if (["saas", "software", "b2b", "tech", "ai", "elektron"].some((word) => q.includes(word))) {
      id = "tech";
    } else if (["sport", "fitness", "outdoor", "trenér", "kolo"].some((word) => q.includes(word))) {
      id = "sport";
    } else if (["katalog", "market", "sortiment", "marketplace"].some((word) => q.includes(word))) {
      id = "market";
    } else if (["handmade", "tvůr", "creator", "osobní značka", "atelier"].some((word) => q.includes(word))) {
      id = "creator";
    }

    selectStyle(id);
  };

  return (
    <section className={"showcase shell template-showcase template-showcase-" + selected} id="styles" aria-label="Výběr stylu webu">
          {showHeading && <div className="template-showcase-heading"><div><p>NAJDĚTE SVŮJ VIZUÁLNÍ SMĚR</p><h2>Který styl je nejvíc vy?</h2></div><p>Osm vizuálních směrů. Osm různých nálad. Vyberte si ten, ve kterém poznáte svou značku.</p></div>}
          <div className="style-zone">
            <StyleTabs selected={selected} onSelect={selectStyle} tabsId={tabsId} />

            <div className="showcase-grid" role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${selected}`}>
              <div className="style-preview-stage" data-style={selected} data-label={current.label}>
                <div className="style-emotion" aria-live="polite"><span>{String(styles.findIndex(style => style.id === selected) + 1).padStart(2, "0")} / 08 · {current.label}</span><strong>{current.emotion}</strong></div>
                <StoreMockup key={selected} current={current} />
              </div>

              <aside className="advisor">
                <h3>Zatím se v žádném nevidíte?</h3>
                <p>Napište, co děláte a pro koho. Nabídneme vám první inspiraci podle oboru.</p>
                <div className="prompt-wrap">
                  <textarea
                    value={prompt}
                    maxLength={300}
                    onChange={(event) => setPrompt(event.target.value)}
                    aria-label="Popis vašeho podnikání"
                    placeholder="Například: Vedeme malou čajovnu. Chceme klidný, přírodní web s nabídkou čajů a rezervací."
                  />
                  <small>{prompt.length}/300</small>
                </div>

                <div className="ai-result-card">
                  <span>✦ První inspirace pro váš projekt</span>
                  <strong>{current.label}</strong>
                  <p>{current.subtitle}</p>
                  <div className="chips">
                    {current.tags.map(tag => <i key={tag}>{tag}</i>)}
                  </div>
                  <button type="button" onClick={recommend} disabled={prompt.trim().length < 3}>Najít první směr <span>→</span></button>
                </div>
              </aside>
            </div>

            <p className="template-demo-caption">Vizuální koncept pro inspiraci. Značky, produkty a ceny jsou ilustrační.</p>
            <div className="template-enquiry"><div><small>TENTO SMĚR VÁM SEDÍ?</small><h2>{current.label} / {current.caption}</h2><p>Vezměme ho jako začátek. Barvy, obsah i funkce přizpůsobíme tomu, co potřebuje vaše značka.</p></div><a className="primary" href={"/?styl=" + selected + "#audit"}>Chci web v tomto duchu <span aria-hidden="true">↗</span></a></div>
            <div className="mini-grid">
              {miniStyles.map((id) => {
                const style = styles.find((item) => item.id === id)!;
                return <MiniCard key={id} style={style} selected={selected === id} onSelect={setSelected} />;
              })}
            </div>

          </div>
    </section>
  );
}
